import { reactive } from 'vue'
import api from '../api/index'

/**
 * 青少年模式统一状态源（服务端/数据库为权威，本地仅缓存展示状态）。
 *
 * 架构约定：
 * - 状态按登录账号绑定，存于服务端 fa_youth_settings 表；
 * - 所有组件只从这里读取/操作，不再直接读写 localStorage 的青少年 key；
 * - 倒计时展示用本地推算（countdownEnd - Date.now()），服务端定期对账；
 * - 服务端在漫画接口中执行真实过滤与拦截，前端只负责展示与交互。
 */

export const youthSettingsChangedEvent = 'youth-protection-settings-changed'

// 服务端青少年限制业务码（request.js 会原样透传这些码）
export const YOUTH_CODE_LOCKED = 1001
export const YOUTH_CODE_CONTENT = 1002
export const YOUTH_CODE_NIGHT = 1003
export const YOUTH_CODE_PAID = 1004
export const YOUTH_CODE_COMMUNITY = 1005

export const youthErrorText = {
  1001: '青少年模式使用时长已满，请先完成密码认证。',
  1002: '该作品在青少年模式下不可查看。',
  1003: '青少年模式夜间(22:00-06:00)不可阅读。',
  1004: '青少年模式下不可阅读付费章节。',
  1005: '青少年模式下社区互动受到限制，请先完成监护人密码认证。'
}

const youthSessionMs = 2 * 60 * 60 * 1000
const SYNC_INTERVAL_MS = 60 * 1000

const state = reactive({
  initialized: false,
  loading: false,
  loggedIn: false,
  enabled: false,
  locked: false,
  countdownEnd: 0,      // 服务端倒计时结束时间戳（秒）
  remaining: youthSessionMs, // 本地推算剩余毫秒
  skewMs: 0,            // 本机时钟与服务端的偏差校准（毫秒）
  contentLevel: 'teen',
  ageGroup: 'under18',
  rules: [],
  hasGuardianCode: false,
  dailyLimit: 120,
  serverTime: 0,
  syncError: false
})

let tickTimer = null
let syncTimer = null

function stopTimers() {
  if (tickTimer) {
    clearInterval(tickTimer)
    tickTimer = null
  }
  if (syncTimer) {
    clearInterval(syncTimer)
    syncTimer = null
  }
}

function updateRemaining() {
  if (!state.enabled || !state.countdownEnd) {
    state.remaining = youthSessionMs
    return
  }
  // 用服务端返回的 remaining 校准本机时钟偏差，避免 02:00:03 这类“多出几秒”的显示
  const left = state.countdownEnd * 1000 - Date.now() - state.skewMs
  state.remaining = Math.max(0, left)
  if (state.countdownEnd > 0 && left <= 0) {
    state.locked = true
  }
}

function applyServerState(data) {
  state.enabled = Boolean(data.enabled)
  state.locked = Boolean(data.locked)
  state.countdownEnd = Number(data.countdownEnd || 0)
  state.contentLevel = data.contentLevel || 'teen'
  state.ageGroup = data.ageGroup || 'under18'
  state.rules = Array.isArray(data.rules) ? data.rules : []
  state.hasGuardianCode = Boolean(data.hasGuardianCode)
  state.dailyLimit = Number(data.dailyLimit || 120)
  state.serverTime = Number(data.serverTime || 0)
  state.syncError = false
  // 服务端权威剩余时间 → 计算时钟偏差（含网络延迟），此后本地倒计时都以它为准
  if (state.enabled && state.countdownEnd > 0 && typeof data.remaining === 'number') {
    state.skewMs = state.countdownEnd * 1000 - Date.now() - data.remaining * 1000
  } else {
    state.skewMs = 0
  }
  updateRemaining()
}

function ensureTimers() {
  stopTimers()
  if (!state.enabled) {
    return
  }
  tickTimer = setInterval(updateRemaining, 1000)
  syncTimer = setInterval(refreshYouthState, SYNC_INTERVAL_MS)
}

function notifyChanged() {
  window.dispatchEvent(new Event(youthSettingsChangedEvent))
}

/**
 * 与服务端对账青少年状态。未登录时视为未开启。
 */
export async function refreshYouthState() {
  const token = localStorage.getItem('token')
  state.loggedIn = Boolean(token)
  if (!token) {
    state.enabled = false
    state.locked = false
    state.countdownEnd = 0
    updateRemaining()
    stopTimers()
    return
  }

  state.loading = true
  try {
    const res = await api.getYouthStatus()
    if (res && res.code === 1 && res.data) {
      applyServerState(res.data)
      ensureTimers()
    } else {
      state.enabled = false
      state.locked = false
      updateRemaining()
      stopTimers()
    }
  } catch (error) {
    state.syncError = true
  } finally {
    state.loading = false
  }
}

/**
 * 应用启动时初始化一次（幂等）
 */
export function initYouthStore() {
  if (state.initialized) {
    return
  }
  state.initialized = true
  window.addEventListener('focus', refreshYouthState)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshYouthState()
    }
  })
  refreshYouthState()
}

/**
 * 保存权限设置（监护人手机号、内容等级、规则、密码）
 */
export async function saveYouthSettings(payload) {
  const res = await api.saveYouthSettings(payload)
  if (res && res.code === 1 && res.data) {
    applyServerState(res.data)
    ensureTimers()
  }
  notifyChanged()
  return res
}

/**
 * 开启青少年模式（需要密码确认；首次开启时以该密码建立监护人密码）
 */
export async function enableYouthMode(guardianCode) {
  const res = await api.enableYouthMode(guardianCode)
  if (res && res.code === 1 && res.data) {
    applyServerState(res.data)
    ensureTimers()
  }
  notifyChanged()
  return res
}

/**
 * 密码认证，永久关闭青少年模式
 */
export async function authYouthMode(guardianCode) {
  const res = await api.authYouthMode(guardianCode)
  if (res && res.code === 1 && res.data) {
    applyServerState(res.data)
    stopTimers()
  }
  notifyChanged()
  return res
}

/**
 * 恢复默认配置
 */
export async function resetYouthSettings(guardianCode) {
  const res = await api.resetYouthSettings(guardianCode)
  if (res && res.code === 1 && res.data) {
    applyServerState(res.data)
    stopTimers()
  }
  notifyChanged()
  return res
}

/** 青少年模式是否开启 */
export function isYouthModeActive() {
  return state.enabled
}

/** 使用时长是否已用尽（全站锁定） */
export function isYouthLocked() {
  return state.enabled && state.locked
}

/** 社区发帖/评论是否受限（规则 communityLimit 或已锁定） */
export function isYouthCommunityRestricted() {
  return state.enabled && (state.locked || state.rules.includes('communityLimit'))
}

/** 是否禁止充值/开通会员（跟随付费确认规则，锁定时始终禁止） */
export function isYouthRechargeRestricted() {
  return state.enabled && (state.locked || state.rules.includes('paidConfirm'))
}

/** 剩余可用时间（毫秒），用于展示 */
export function youthRemainingMs() {
  return state.remaining
}

export const youthStore = state
